/**
 * COPY STRATEGY — ParkCostPage
 *
 * Awareness:      Unaware → Problem-Aware (Stage 1→2/5)
 *                 Reader works through a park and considers it "convenient."
 *                 Doesn't realize the true annual cost of that convenience.
 *
 * Sophistication: Stage 5 — Identification
 *                 Identity play — "ko bijote?" challenges their self-image.
 *                 Positions going independent as the brave, professional move.
 *                 Park = fear tax. Finhudas = independence tool.
 *
 * Avatar:         Driver working through a fleet park, paying 5%+ commission.
 *                 Afraid of taxes and paperwork. Earning €1000–2500/month.
 *                 Needs emotional push more than information.
 *
 * Hook:           Identity challenge — "Kiek tūkstančių atiduodate už tai, ko bijote?"
 * Structure:      Cost table → What park actually does → What you can do yourself → CTA
 * Proof:          Annual loss table (€600–€1800/year), park vs Finhudas cost comparison
 * Funnel:         Gateway to Finhudas signup, leads to Bolt accounting and tax articles
 */
import { motion } from 'framer-motion'
import { PiCheckCircle, PiWarning } from 'react-icons/pi'

import HeaderBack from '@/components/dashboard/Header/HeaderBack'
import MotionText from '@/components/common/Motion/MotionText'
import MotionDiv from '@/components/common/Motion/MotionDiv'
import ButtonLink from '@components/common/Button/ButtonLink'
import FinhudasCTA from '@/components/common/Blog/FinhudasCTA'
import RelatedArticles from '@/components/common/Blog/RelatedArticles'
import { usePageSetup } from '@/hooks/usePageSetup'

const ParkCostPage = () => {
  usePageSetup('Tikroji darbo „per parką" kaina', {
    scroll: true,
    description: 'Parkas ima 5–15% komisinį — tai €600–1 800 per metus. Ką tiksliai gauname už šiuos pinigus? Palyginimas: parkas vs individuali veikla. Sužinokite tikrąją „patogumo" kainą.',
    type: 'article',
    datePublished: '2026-02-25',
  })

  return (
    <>
      <HeaderBack to="/blog" />
      <div className="min-h-full bg-white px-5 pb-8 flex flex-col">

        <MotionText as="h1" className="text-zinc-900 text-2xl font-medium mt-3 mb-2">
          Tikroji darbo „per parką" kaina: Kiek tūkstančių eurų atiduodate už tai, ko bijote?
        </MotionText>

        <MotionDiv className="flex items-center gap-2 mb-6">
          <span className="text-xs font-medium text-zinc-500">Apskaita</span>
          <span className="text-xs text-zinc-300">•</span>
          <span className="text-xs text-zinc-500">6 min skaitymo</span>
        </MotionDiv>

        <MotionText as="p" className="text-zinc-700 text-base mb-2">
          Jei vis dar dirbate per parką, tikriausiai sakote: „Man taip patogiau" arba „Nenoriu vargintis su mokesčiais."
        </MotionText>

        <MotionText as="p" className="text-zinc-700 text-base mb-2">
          Bet ar tikrai žinote, kiek tas „patogumas" kainuoja?
        </MotionText>

        <MotionText as="h2" className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2">
          Kiek prarandate per metus?
        </MotionText>

        <MotionDiv className="border border-zinc-250 rounded-xl overflow-hidden mb-4">
          <div className="grid grid-cols-3 bg-zinc-50 p-3 border-b border-zinc-250">
            <MotionText as="p" className="text-zinc-500 text-xs font-medium">Pajamos/mėn.</MotionText>
            <MotionText as="p" className="text-zinc-500 text-xs font-medium">Parkas (5%)</MotionText>
            <MotionText as="p" className="text-zinc-500 text-xs font-medium">Metinis nuostolis</MotionText>
          </div>

          {[
            { income: '1 000 €', park: '50 €', annual: '600 €' },
            { income: '1 500 €', park: '75 €', annual: '900 €' },
            { income: '2 000 €', park: '100 €', annual: '1 200 €' },
            { income: '2 500 €', park: '125 €', annual: '1 500 €' },
            { income: '3 000 €', park: '150 €', annual: '1 800 €' },
          ].map((row, i) => (
            <div key={i} className={`grid grid-cols-3 p-3 ${i % 2 === 0 ? 'bg-white' : 'bg-zinc-50'} ${i < 4 ? 'border-b border-zinc-100' : ''}`}>
              <MotionText as="p" className="text-zinc-900 text-xs">{row.income}</MotionText>
              <MotionText as="p" className="text-red-600 text-xs font-medium">−{row.park}</MotionText>
              <MotionText as="p" className="text-red-600 text-xs font-medium">{row.annual}</MotionText>
            </div>
          ))}
        </MotionDiv>

        <MotionText as="p" className="text-zinc-700 text-base">
          Jei uždirbi 2 000€ per mėnesį, per metus atiduodi parkui <strong>1 200€</strong>. Už tą sumą galėtumėte atostogauti su šeima.
        </MotionText>

        <MotionText as="h2" className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2">
          Ką parkas iš tikrųjų daro?
        </MotionText>

        <MotionText as="p" className="text-zinc-700 text-base mb-2">
          Parkas atlieka bazinę PVM deklaraciją ir pajamų raportavimą. Tai viskas. Jis <strong>nedaro</strong>:
        </MotionText>

        <MotionDiv className="p-4 bg-amber-50 border border-amber-200 rounded-xl mb-4">
          <div className="flex gap-3">
            <PiWarning className="min-w-6 h-6 text-amber-700" />
            <div>
              <MotionText as="h3" className="text-amber-900 text-sm font-medium mb-2">
                Ko parkas NEDARO
              </MotionText>
              <ul className="space-y-1.5">
                <li className="text-amber-800 text-sm">
                  ❌ Nelygina 30% normos su faktinėmis išlaidomis
                </li>
                <li className="text-amber-800 text-sm">
                  ❌ Neseka jūsų kuro, amortizacijos ir kitų išlaidų
                </li>
                <li className="text-amber-800 text-sm">
                  ❌ Nerodo mokesčių kalendoriaus su terminais
                </li>
                <li className="text-amber-800 text-sm">
                  ❌ Neoptimizuoja jūsų mokesčių strategijos
                </li>
                <li className="text-amber-800 text-sm">
                  ❌ Neduoda jums kontrolės dėl savo finansų
                </li>
              </ul>
            </div>
          </div>
        </MotionDiv>

        <MotionText as="p" className="text-zinc-700 text-base font-medium">
          Parkas — tai baimės mokestis. Jūs mokate ne už paslaugą, o už tai, kad BIJOTE patys tvarkyti apskaitą.
        </MotionText>

        <MotionText as="h2" className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2">
          Parkas vs. Finhudas
        </MotionText>

        <MotionDiv className="border border-zinc-250 rounded-xl overflow-hidden mb-4">
          <div className="grid grid-cols-3 bg-zinc-50 p-3 border-b border-zinc-250">
            <MotionText as="p" className="text-zinc-500 text-xs font-medium">Funkcija</MotionText>
            <MotionText as="p" className="text-zinc-500 text-xs font-medium">Parkas</MotionText>
            <MotionText as="p" className="text-zinc-500 text-xs font-medium">Finhudas</MotionText>
          </div>

          {[
            { feature: 'Mėnesinis mokestis', park: '50–150€', finhudas: '7€' },
            { feature: 'Atvirkštinis PVM', park: 'Kartą per mėnesį', finhudas: 'Auto per sąskaitą' },
            { feature: '30% vs faktinės', park: 'Ne', finhudas: 'Taip + sutaupymas' },
            { feature: 'Mokesčių kalendorius', park: 'Ne', finhudas: 'Taip' },
            { feature: 'Išlaidų sekimas', park: 'Ne', finhudas: 'OCR nuskaitymas' },
            { feature: 'Jūsų duomenų kontrolė', park: 'Ne', finhudas: 'Pilna kontrolė' },
          ].map((row, i) => (
            <div key={i} className={`grid grid-cols-3 p-3 ${i % 2 === 0 ? 'bg-white' : 'bg-zinc-50'} ${i < 5 ? 'border-b border-zinc-100' : ''}`}>
              <MotionText as="p" className="text-zinc-900 text-xs font-medium">{row.feature}</MotionText>
              <MotionText as="p" className="text-zinc-600 text-xs">{row.park}</MotionText>
              <MotionText as="p" className="text-blue-600 text-xs font-medium">{row.finhudas}</MotionText>
            </div>
          ))}
        </MotionDiv>

        <MotionText as="h2" className="text-zinc-900 text-xl font-medium border-t border-zinc-200 mt-6 pt-6 mb-2">
          Kaip atidaryti savo IV per 3 žingsnius
        </MotionText>

        <MotionDiv className="bg-zinc-100 rounded-2xl overflow-hidden mb-4">
          <div className="p-5 space-y-5">
            <div className="flex gap-4">
              <div className="flex flex-col items-center">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-800 text-zinc-50 rounded-full flex items-center justify-center text-xs font-medium">
                  1
                </div>
                <div className="flex-1 w-px bg-zinc-300 my-1.5" />
              </div>
              <div className="pb-1">
                <MotionText as="p" className="text-zinc-900 text-sm font-medium mb-0.5">
                  Gaukite IV pažymą iš VMI
                </MotionText>
                <MotionText as="p" className="text-zinc-500 text-xs">
                  Prisijunkite prie Mano VMI ir užpildykite prašymą. Procesas užtrunka ~10 minučių.
                </MotionText>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex flex-col items-center">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-800 text-zinc-50 rounded-full flex items-center justify-center text-xs font-medium">
                  2
                </div>
                <div className="flex-1 w-px bg-zinc-300 my-1.5" />
              </div>
              <div className="pb-1">
                <MotionText as="p" className="text-zinc-900 text-sm font-medium mb-0.5">
                  Pradėkite deklaruoti Atvirkštinį PVM
                </MotionText>
                <MotionText as="p" className="text-zinc-500 text-xs">
                  Kas mėnesį pildykite PVM101 deklaraciją VMI portale už Bolt komisinius (21% PVM). PVM mokėtoju registruotis nereikia, kol pajamos neviršija 45 000 €/metus.
                </MotionText>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex flex-col items-center">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-800 text-zinc-50 rounded-full flex items-center justify-center text-xs font-medium">
                  3
                </div>
              </div>
              <div>
                <MotionText as="p" className="text-zinc-900 text-sm font-medium mb-0.5">
                  Įdiekite Finhudas ir įkelkite pirmą ataskaitą
                </MotionText>
                <MotionText as="p" className="text-zinc-500 text-xs">
                  Parsisiųskite Bolt ataskaitą iš partners.bolt.eu → įkelkite į Finhudas. Sistema viską apskaičiuos automatiškai.
                </MotionText>
              </div>
            </div>
          </div>
        </MotionDiv>

        <MotionDiv className="py-4 bg-zinc-50 rounded-xl mb-4">
          <MotionText as="h3" className="text-zinc-900 text-base font-medium mb-2">
            Matematika
          </MotionText>
          <MotionDiv className="border-t border-zinc-250 pt-3 mt-3">
            <MotionText as="p" className="text-zinc-700 text-sm font-mono">
              Bruto pajamos: 1 500 €/mėn.
            </MotionText>
            <MotionText as="p" className="text-red-600 text-sm font-mono">
              Parkas pasiima 5%: −75 €
            </MotionText>
            <MotionText as="p" className="text-blue-600 text-sm font-mono">
              Finhudas kainuoja: −7 €
            </MotionText>
            <MotionText as="p" className="text-zinc-700 text-sm font-mono border-t border-zinc-250 pt-2 mt-2 font-medium">
              Skirtumas: 68 € per mėnesį = 816 € per metus
            </MotionText>
          </MotionDiv>
        </MotionDiv>

        <MotionDiv className="p-4 bg-green-50 border border-green-200 rounded-xl">
          <div className="flex gap-3">
            <PiCheckCircle className="min-w-6 h-6 text-green-700" />
            <div>
              <MotionText as="h3" className="text-green-900 text-sm font-medium mb-2">
                Taip pat paprasta kaip dirbti per parką
              </MotionText>
              <MotionText as="p" className="text-green-800 text-sm">
                Finhudas automatizuoja Atvirkštinį PVM ir mokesčių skaičiavimus. Jums belieka įkelti Bolt ataskaitą — viskas kita atliekama automatiškai. Nereikia jokių buhalterinių žinių.
              </MotionText>
            </div>
          </div>
        </MotionDiv>

        <MotionText as="p" className="text-zinc-700 text-base mt-6 mb-2">
          Profesionalūs vairuotojai seniai suprato: nėra prasmės mokėti tarpininkui.
        </MotionText>

        <MotionText as="p" className="text-zinc-700 text-base mb-6">
          Pabandykite Finhudas — jei nepatiks, visada galite grįžti pas parką. Bet pirmiausia pažiūrėkite, kiek iš tikrųjų prarandate.
        </MotionText>

        <FinhudasCTA awareness="solution" />

        <RelatedArticles articles={[
          { slug: 'atvirkstinis-pvm-pavezejams', title: 'Atvirkštinis PVM: mokestis, kurį dauguma pamiršta' },
          { slug: 'kodel-moketi-buhalteriui-40-eur', title: 'Kodėl mokėti buhalteriui 40€ yra pinigų deginimas' },
          { slug: 'pavezejo-mokesciai-gpm-psd-vsd', title: 'Mokesčiai pavežėjui 2026: GPM, PSD, VSD ir PVM' }
        ]} />

        <motion.div className="mt-10" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
          <ButtonLink variant="secondary" to="/blog">Grįžti į straipsnius</ButtonLink>
        </motion.div>
      </div>
    </>
  )
}

export default ParkCostPage
